import {
  Box,
  Chip,
  Button,
  Dialog,
  Backdrop,
  useTheme,
  TextField,
  FormLabel,
  DialogTitle,
  FormControl,
  Autocomplete,
  DialogActions,
  DialogContent,
  svgIconClasses,
  CircularProgress,
  DialogContentText,
} from '@mui/material';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import apiService from 'library/api-service';
import { GridRowId } from '@mui/x-data-grid-premium';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { gray } from 'providers/theme/themePrimitives';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCallback, useEffect, useState } from 'react';
import loaderLogoSrc from 'assets/images/idecon-loader-logo.png';

export type UpdateEffortContactFormParams = {
  id: number;
  title: string;
  emails: string[];
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

const getAllEmailErrors = (error: any): string[] => {
  if (!error) return [];

  if (Array.isArray(error)) {
    return error.map((e: any) => e?.message).filter(Boolean);
  }

  return [error.message];
};

type UpdateEffortContactDialogProps = {
  open: boolean;
  effortContactId: GridRowId;
  handleClose: () => void;
  refresh: () => Promise<void>;
};

const UpdateEffortContactDialog = ({
  open,
  effortContactId,
  handleClose: closeDialog,
  refresh,
}: UpdateEffortContactDialogProps) => {
  // other hooks
  const intl = useIntl();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  // useState hooks
  const [loading, setLoading] = useState(false);

  // form validations & form state
  const schema = yup.object({
    title: yup
      .string()
      .trim()
      .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
    emails: yup
      .array()
      .of(
        yup
          .string()
          .trim()
          .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' }))
          .matches(
            emailRegex,
            intl.formatMessage({ id: 'validations.invalidEmailFormat', defaultMessage: 'Geçersiz e-posta formatı' })
          )
      )
      .min(
        1,
        intl.formatMessage({ id: 'validations.emails.minOne', defaultMessage: 'En az 1 e-posta adresi girilmelidir.' })
      )
      .test(
        'unique',
        intl.formatMessage({
          id: 'validations.emails.duplicate',
          defaultMessage: 'E-posta listesinde tekrar eden adresler var.',
        }),
        (arr) => {
          const list = (arr ?? []).map((x) => x.trim().toLowerCase());
          return new Set(list).size === list.length;
        }
      )
      .required(),
  });

  const { reset, control, setValue, handleSubmit } = useForm<UpdateEffortContactFormParams>({
    defaultValues: { id: 0, title: '', emails: [] },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    // @ts-ignore
    resolver: yupResolver(schema),
    shouldFocusError: true,
  });

  // sync helper functions
  const handleClose = () => {
    closeDialog();
    reset();
  };

  // async helper functions
  const onSubmit = async (data: UpdateEffortContactFormParams) => {
    try {
      setLoading(true);

      const response = await apiService.updateEffortContact(data);

      if (response.isSuccess) {
        enqueueSnackbar(
          <FormattedMessage
            id="effortContacts.updateEffortContactCompleted"
            defaultMessage="Efor Muhatabı düzenleme işlemi başarıyla tamamlandı."
          />,
          { variant: 'success' }
        );
        handleClose();
        refresh();
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const getSpecificEffortContact = useCallback(async () => {
    try {
      setLoading(true);

      const response = await apiService.getSpecificEffortContact(effortContactId as number);

      if (response.isSuccess) {
        setValue('id', effortContactId as number);
        setValue('title', response.data.title);
        setValue('emails', response.data.emails ?? []);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, [effortContactId, setValue]);

  // useEffect hooks
  useEffect(() => {
    if (open && effortContactId) {
      getSpecificEffortContact();
    }
  }, [open, effortContactId, getSpecificEffortContact]);

  return (
    <Dialog maxWidth="lg" open={open} onClose={handleClose}>
      <DialogTitle>
        <FormattedMessage id="effortContacts.updateEffortContact" defaultMessage="Efor Muhatabı Düzenle" />
      </DialogTitle>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent sx={{ display: 'grid', gap: 2, width: '100%' }}>
          <DialogContentText>
            <FormattedMessage
              id="effortContacts.updateEffortContactText"
              defaultMessage="İlgili alanları değiştirip devam ederek seçili Efor Muhatabı için düzenleme yapabilirsiniz."
            />
          </DialogContentText>
          <FormControl>
            <FormLabel htmlFor="title">
              <FormattedMessage id="gibTypes.title" defaultMessage="Adı" />
            </FormLabel>
            <Controller
              name="title"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField {...field} fullWidth autoFocus size="small" error={!!error} helperText={error?.message} />
              )}
            />
          </FormControl>
          <FormControl>
            <FormLabel htmlFor="emails">
              <FormattedMessage id="effortContacts.emails" defaultMessage="E-Postalar" />
            </FormLabel>
            <Controller
              name="emails"
              control={control}
              render={({ field, fieldState: { error } }) => {
                const errorMessage = getAllEmailErrors(error);

                return (
                  <Autocomplete
                    size="small"
                    multiple
                    freeSolo
                    options={[]}
                    value={field.value ?? []}
                    onChange={(_, newValue) => {
                      const normalized = (newValue as string[])
                        .map((x) => (typeof x === 'string' ? x : String(x)))
                        .map((x) => x.trim())
                        .filter(Boolean);

                      field.onChange(normalized);
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={option}
                          {...getTagProps({ index })}
                          key={`${option}-${index}`}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        sx={{
                          '& .MuiButtonBase-root': {
                            border: 'none',
                            width: 'fit-content',
                            height: '2.25rem',
                            padding: '0.25rem',
                            [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                            },
                          },
                          '& .MuiInputBase-root': {
                            height: 'auto',
                          },
                        }}
                        placeholder={intl.formatMessage({
                          id: 'effortContacts.pushEnterAfterWritingEmail',
                          defaultMessage: 'E-Posta yazıp Enter’a basın',
                        })}
                        error={!!error}
                        helperText={errorMessage}
                      />
                    )}
                  />
                );
              }}
            />
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ pb: 3, px: 3 }}>
          <Button onClick={handleClose}>
            <FormattedMessage id="forgotPassword.cancel" defaultMessage="Vazgeç" />
          </Button>
          <Button variant="contained" type="submit">
            <FormattedMessage id="forgotPassword.continue" defaultMessage="Devam" />
          </Button>
        </DialogActions>
      </Box>
      <Backdrop
        sx={{ display: 'flex', flexDirection: 'column', color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}>
        <img src={loaderLogoSrc} alt="loader-logo" />
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
};

export default UpdateEffortContactDialog;
